/**
 * Everything react-querybuilder needs to author a leaf that *quantifies over* a collection field.
 *
 * A collection field's runtime value is a list of tuples, so a leaf on it carries a
 * quantifier (`match.mode`) and a nested group of predicates written in the tuple's own element
 * names — not an operator and an operand. RQB renders exactly that shape: a field declaring
 * `matchModes` gets a match-mode editor in place of its operator and value cells, plus a nested
 * QueryBuilder over the field's `subproperties` whose query is the rule's `value`.
 *
 * Both halves are *derived*, never enumerated. The element vocabulary is the one thing the field
 * catalog cannot publish as `FieldDefinition`s of its own — there is no context member at
 * `claim.computed.communications.direction`, there is one per row — so the provider ships it
 * inside `hints[COLLECTION_HINT_KEY]`, or, for a row that is another context re-rooted onto it,
 * names that context, whose own fields travel in the same catalog; this module reads either. A
 * second collection field from any provider is authorable with no change here.
 *
 * An element can itself be a collection — a party's `computed.injuries` on `claim.involved_parties`
 * — and then carries its own block in its own `hints`. It is declared to RQB exactly as a catalog
 * collection field is, with match modes and subproperties of its own, so a leaf on it renders as a
 * match row inside the body, holding a builder of its own; the chrome below is what every one of
 * those builders renders with, at every depth.
 *
 * The sub-leaf `field` spelling is the load-bearing contract. RQB writes it straight from the
 * subproperty identifier, so naming each subproperty by its element name relative to the row
 * (`direction`, `variant.involved_vehicle.is_total_loss`) is what makes the emitted tree the one
 * `_compile_collection_leaf` expects: that function aliases the name into `_c0.direction` itself,
 * through `_rewrite_element_fields`, and rejects a sub-leaf that arrives already aliased.
 */

import type {
  Classnames,
  Controls,
  Field,
  FullField,
  FullOption,
  QueryBuilderProps,
  RuleGroupType,
  RuleType,
} from 'react-querybuilder';

import {
  AddGroupAction,
  AddRuleAction,
  CombinatorSelector,
  RemoveGroupAction,
  RemoveRuleAction,
  SubFieldSelector,
  SubOperatorSelector,
} from './controls';
import type { MatchModeOption } from './matchModes';
import { matchModesFor } from './matchModes';
import { operatorsForField } from './operators';
import type { CollectionBlockCarrier, EnumOption, FieldDefinitionDto, FieldType } from './types';
import { collectionElements, FIELD_TYPE, VARIANT_HINT_KEY } from './types';
import { SubValueEditor } from './ValueEditor';

export type SubpropertyField = Field & {
  field_type: FieldType;
  enum_values?: EnumOption[] | null;
  group?: string[];
  hints?: Record<string, unknown>;
};

/** Whether a leaf on a field quantifies over it, as react-querybuilder reads that off the field.
 *
 *  A `collection` field — in the catalog, or as an element of another collection — declares the
 *  modes its mode cell offers and the elements its body is written in. `matchModes` is `false`
 *  rather than omitted for every other field: `getMatchModes` reads `fieldData.matchModes ?? false`,
 *  and an explicit `false` says "this field is compared, not quantified over" at the declaration
 *  rather than by omission. Shared by the root's field list and every body's, so a collection is
 *  declared the same way at any depth. */
export const quantifierProps = (
  field: CollectionBlockCarrier & { field_type: FieldType },
  catalog: ReadonlyArray<FieldDefinitionDto>
): { matchModes: MatchModeOption[] | false; subproperties?: SubpropertyField[] } =>
  field.field_type === FIELD_TYPE.Collection
    ? { matchModes: matchModesFor(field), subproperties: collectionSubproperties(field, catalog) }
    : { matchModes: false };

/** The collection's elements as react-querybuilder fields for the nested builder.
 *
 *  `name` is the element name relative to the row and not a catalog path — see the module
 *  docblock; it is what lands in the saved sub-leaf. `operators` are what the root offers a field
 *  of the element's `field_type` and type guard, because the compiler dispatches a sub-leaf
 *  through the identical `_compile_leaf` type dispatch: a `multiselect` element gets the
 *  set-overlap operators, a `datetime` one the six comparisons and an offset-valued right-hand
 *  side, a type-guarded `user` one the presence checks alone. `field_type`, `enum_values`, `group`
 *  and `hints` ride along as custom props: RQB hands them to our cells as `fieldData`, and the
 *  body's field picker nests the elements by `group`, as the root picker nests the catalog. An
 *  element that is itself a collection gets the root's `quantifierProps`, read off its own block
 *  and resolved against the same `catalog`.
 *
 *  Cast for the same two reasons the root builder casts its own `rqbFields`: the custom props are
 *  not on `Field`, and `OperatorOption` is an interface, so it has no implicit index signature to
 *  satisfy the option shape v8 declares. */
export const collectionSubproperties = (
  field: CollectionBlockCarrier,
  catalog: ReadonlyArray<FieldDefinitionDto>
): SubpropertyField[] =>
  collectionElements(field, catalog).map(
    (element) =>
      ({
        name: element.name,
        label: element.label || element.name,
        operators: operatorsForField(element.field_type, Boolean(element.hints?.[VARIANT_HINT_KEY])),
        ...quantifierProps(element, catalog),
        field_type: element.field_type,
        enum_values: element.enum_values,
        group: element.group,
        hints: element.hints,
      }) as SubpropertyField
  );

/** The chrome the nested builder renders with.
 *
 *  It is not inherited: RQB builds the sub-query from `getSubQueryBuilderProps(field)` and nothing
 *  else, so without this the body renders RQB's stock native selects and buttons inside a dialog
 *  built entirely from CMS Core.
 *
 *  Three of the root's adapters are deliberately *not* reused. The root's `FieldSelector`,
 *  `OperatorSelector` and `ValueEditor` each open with an interception for a leaf the catalog
 *  cannot resolve, and inside a match body every field is an element name — which is exactly what
 *  the registry does not serve — so the root's own answer would mark every sub-leaf unresolvable
 *  and render the whole body read-only, which is the row this module exists to replace.
 *  `SubFieldSelector` / `SubOperatorSelector` / `SubValueEditor` are the same controls without
 *  that branch, and the root cells delegate to them once their interceptions have not fired. */
const SUB_CONTROL_ELEMENTS: Partial<Controls<FullField, string>> = {
  fieldSelector: SubFieldSelector,
  operatorSelector: SubOperatorSelector,
  combinatorSelector: CombinatorSelector,
  valueEditor: SubValueEditor,
  addRuleAction: AddRuleAction,
  addGroupAction: AddGroupAction,
  removeRuleAction: RemoveRuleAction,
  removeGroupAction: RemoveGroupAction,
};

/** The nested group's layout. The same three-plus-trash grid as the root, one step in from the
 *  left and without the root's card fill, so the body reads as *inside* the match row rather than
 *  as a second condition card beside it. */
const SUB_CONTROL_CLASSNAMES: Partial<Classnames> = {
  ruleGroup: 'flex flex-col gap-16 pl-20 border-0 border-l-2 border-solid border-slate-500',
  header: 'flex flex-wrap items-center gap-12',
  body: 'flex flex-col gap-16 [&:empty]:hidden',
  rule: 'grid grid-cols-[minmax(160px,3fr)_minmax(120px,2fr)_minmax(180px,5fr)_32px] gap-16 items-start',
  fields: '[&>*]:w-full',
  operators: '[&>*]:w-full',
  value: '[&>*]:w-full',
  removeRule: 'col-start-4 self-end justify-self-center',
};

/** Where a match row puts each of its five children, in whichever builder renders it.
 *
 *  RQB renders them in this order: field, match-mode, sub-query *header*, remove, sub-query *body*.
 *  Every child is placed explicitly, because the two that are not — the header and the remove
 *  button — collide: `rule`'s `removeRule` pins the trash to `col-start-4`, and a header left to
 *  span into that track pushes the trash onto an implicit row of its own, opening a ~130px band of
 *  dead space with the icon stranded in it.
 *
 *  Line 1  Field | Match mode | (spacer) | trash      — still scans as a condition, and the trash
 *                                                       stays in the column every sibling row uses.
 *  Line 2  the sub-query's combinator + Add buttons, full width so they lay out in a row rather
 *          than wrapping a two-word button onto two lines in a narrow column.
 *  Line 3  the predicates themselves, full width.
 *
 *  Only the placement: each builder pairs it with column tracks of its own, the root's and a body's
 *  ordinary rows being different grids. */
export const MATCH_ROW_PLACEMENT = [
  // Keeps the base class's `col-start-4` but pins the row, which is the half that was missing.
  '[&>*:nth-child(4)]:row-start-1',
  '[&>*:nth-child(3)]:col-span-4 [&>*:nth-child(3)]:row-start-2',
  '[&>*:nth-child(5)]:col-span-4 [&>*:nth-child(5)]:row-start-3',
  // Both blocks sit behind a rule in the nested-group border colour, so the predicates read as
  // this leaf's body rather than as siblings of it — the same "a visible border communicates
  // nesting" convention `ruleGroup` uses, which RQB cannot apply here because it renders the
  // sub-query's header and body as two separate children of the rule rather than inside one
  // group element. `border-0` leads because Tailwind preflight is off in this project: a bare
  // `border-solid` lights all four sides at the default medium width.
  '[&>*:nth-child(3)]:border-0 [&>*:nth-child(3)]:border-l-2 [&>*:nth-child(3)]:border-solid',
  '[&>*:nth-child(3)]:border-slate-800 [&>*:nth-child(3)]:pl-16',
  '[&>*:nth-child(5)]:border-0 [&>*:nth-child(5)]:border-l-2 [&>*:nth-child(5)]:border-solid',
  '[&>*:nth-child(5)]:border-slate-800 [&>*:nth-child(5)]:pl-16',
].join(' ');

/** Whether react-querybuilder renders a rule on this field as a match row — five children rather
 *  than four.
 *
 *  Keyed on the field and not on `rule.match`, because that is what RQB itself keys the row on:
 *  `hasSubQuery` is `getMatchModes(rule.field, { fieldData }).length > 0`, read off the field
 *  declaration. A stored leaf on a collection field carrying no `match` block — an import, a
 *  legacy tree, the state `MatchModeSelector`'s `rule.match?.mode` read and
 *  `validateConditionsTree`'s "needs a match mode" branch both exist for — therefore still renders
 *  five children, and `rule.match` would have left exactly that row in the four-track ordinary-leaf
 *  grid `MATCH_ROW_PLACEMENT` describes. */
export const isMatchRow = (fieldData: FullField): boolean =>
  Array.isArray(fieldData.matchModes) && fieldData.matchModes.length > 0;

/** A body's match row: `rule`'s field track kept, then the mode cell and a spacer, the root match
 *  row's shape on a body's grid. */
const SUB_MATCH_RULE_CLASS = `!grid-cols-[minmax(160px,3fr)_minmax(160px,3fr)_1fr_32px] ${MATCH_ROW_PLACEMENT}`;

const getSubRuleClassname = (_rule: RuleType, { fieldData }: { fieldData: FullField }): string =>
  isMatchRow(fieldData) ? SUB_MATCH_RULE_CLASS : '';

/** Handed to every sub-query, at every depth. A module constant rather than a per-field object
 *  because it is the same for every collection field — the *fields* differ, and RQB takes those
 *  from `subproperties`, not from here — and because RQB re-derives its configuration when this
 *  object is not referentially stable.
 *
 *  It hands itself on: RQB builds a sub-query from the `getSubQueryBuilderProps` of the builder
 *  rendering the match row, so a match row inside a body asks *this* object, not the root. Without
 *  it that body would inherit the root's controls — RQB merges the root's `controlElements` into
 *  every sub-query it renders — whose interceptions read each element name as an unknown catalog
 *  path and render the body read-only. `getRuleClassname` is read from the same place, which is
 *  why a body lays out its own match rows. */
export const SUB_QUERY_BUILDER_PROPS: QueryBuilderProps<RuleGroupType, FullOption, FullOption, FullOption> = {
  controlElements: SUB_CONTROL_ELEMENTS,
  controlClassnames: SUB_CONTROL_CLASSNAMES,
  getSubQueryBuilderProps: () => SUB_QUERY_BUILDER_PROPS,
  getRuleClassname: getSubRuleClassname,
  autoSelectField: false,
  autoSelectOperator: false,
  // Pinned for the same reason the root pins it: a date relation on the `last_datetime` element
  // carries the value it just computed into the same update as the operator, and a reset would
  // immediately discard it.
  resetOnOperatorChange: false,
  listsAsArrays: true,
};
